import { CSSProperties } from "react";
import {
  contrastRatio,
  Default_Background_Primary_Color,
  Default_Background_Secondary_Color,
  Default_Button_Background_Color,
  Default_Button_Text_Color,
  getStudioAccent,
  hexToRgbTriplet,
  isValidHexColor,
  legibleOnDark,
  lightenHex,
  mixHex,
} from "../../../../../../common/infrastructure/utilities/colorUtility";
import { IDesignStudioColors } from "../models/designStudioPresetModel";
import {
  designStudioDefaultColors,
  findMatchingPreset,
  isSamePalette,
  resolvePaletteAccent,
} from "./designStudioPaletteUtility";

export const DESIGN_STUDIO_DEFAULT_NAME = "Planny (по подразбиране)";
export const DESIGN_STUDIO_CUSTOM_NAME = "Собствена палитра";

export const BUTTON_TEXT_MIN_CONTRAST = 4.5;
export const ACCENT_STOP_MIN_CONTRAST = 3;
export const ACCENT_MIDDLE_MIN_CONTRAST = 4.5;

export interface IDesignStudioContrast {
  ratio: number;
  passes: boolean;
}

export interface IDesignStudioAccentLegibility {
  from: IDesignStudioContrast;
  to: IDesignStudioContrast;
  middle: IDesignStudioContrast;
  passes: boolean;
}

/** The name the dialog and the wizard show for a palette. */
export const getDesignStudioPaletteName = (colors: IDesignStudioColors): string => {
  const preset = findMatchingPreset(colors);

  if (preset) {
    return preset.name;
  }

  return isSamePalette(colors, designStudioDefaultColors)
    ? DESIGN_STUDIO_DEFAULT_NAME
    : DESIGN_STUDIO_CUSTOM_NAME;
};

/** "5,7:1". Truncated, not rounded, so a failing 4.49 never reads as the 4.5 it misses. */
export const formatContrastRatio = (ratio: number) =>
  `${(Math.floor(ratio * 10) / 10).toFixed(1).replace(".", ",")}:1`;

const withHash = (hex: string) => (hex.startsWith("#") ? hex : `#${hex}`);

/** A palette colour ready for CSS, or the Planny default when the value is not a colour. */
const paint = (value: string | null | undefined, fallback: string) =>
  withHash(isValidHexColor(value) ? value.trim() : fallback);

const measure = (a: string, b: string, minimum: number): IDesignStudioContrast => {
  const ratio = contrastRatio(a, b);
  return { ratio, passes: ratio >= minimum };
};

export const getButtonLegibility = (colors: IDesignStudioColors): IDesignStudioContrast =>
  measure(
    paint(colors.buttonBackgroundColor, Default_Button_Background_Color),
    paint(colors.buttonTextColor, Default_Button_Text_Color),
    BUTTON_TEXT_MIN_CONTRAST,
  );

/**
 * The accent label crosses the whole gradient, so it is measured at both stops (large,
 * bold text: 3:1) and where the stops meet (body text: 4.5:1).
 */
export const getAccentLegibility = (
  colors: IDesignStudioColors,
): IDesignStudioAccentLegibility => {
  const accent = resolvePaletteAccent(colors);
  const from = measure(accent.from, accent.text, ACCENT_STOP_MIN_CONTRAST);
  const to = measure(accent.to, accent.text, ACCENT_STOP_MIN_CONTRAST);
  const middle = measure(
    mixHex(accent.from, accent.to, 0.5),
    accent.text,
    ACCENT_MIDDLE_MIN_CONTRAST,
  );

  return { from, to, middle, passes: from.passes && to.passes && middle.passes };
};

/**
 * What the app's switches and checkboxes paint with for a studio without an accent
 * (the fallbacks in DefaultToggle / DefaultCheckbox), unlike the "accent" button's
 * purple → pink.
 */
export const LEGACY_CONTROL_FROM = "#5a5999";
export const LEGACY_CONTROL_TO = "#7877c6";
export const LEGACY_CONTROL_KNOB = "#ffffff";

/**
 * Every colour the miniature paints with, as custom properties scoped to the preview
 * root. The canvas stops are the ones public/styles.css mixes for #root.
 */
export const buildDesignStudioPreviewStyle = (
  colors: IDesignStudioColors,
): CSSProperties => {
  const primary = paint(colors.backgroundColorPrimary, Default_Background_Primary_Color);
  const secondary = paint(
    colors.backgroundColorSecondary,
    Default_Background_Secondary_Color,
  );
  const accent = resolvePaletteAccent(colors);
  const ownAccent = getStudioAccent(colors);

  return {
    "--ds-c0": mixHex("#0f0f23", secondary, 0.05),
    "--ds-c25": mixHex("#1a1a2e", secondary, 0.25),
    "--ds-c50": mixHex("#16213e", primary, 0.2),
    "--ds-c75": mixHex("#0f0f23", primary, 0.1),
    "--ds-primary": primary,
    "--ds-secondary": secondary,
    "--ds-button-bg": paint(colors.buttonBackgroundColor, Default_Button_Background_Color),
    "--ds-button-text": paint(colors.buttonTextColor, Default_Button_Text_Color),
    "--ds-accent-from": accent.from,
    "--ds-accent-to": accent.to,
    "--ds-accent-text": accent.text,
    "--ds-accent-glow": `rgba(${hexToRgbTriplet(accent.to)}, 0.32)`,
    // Switches and checkboxes: the palette's accent, or, without one, the darker purple
    // pair the real controls have always had (not the "Потвърди" purple → pink).
    "--ds-control-from": ownAccent?.from ?? LEGACY_CONTROL_FROM,
    "--ds-control-to": ownAccent?.to ?? LEGACY_CONTROL_TO,
    "--ds-control-knob": ownAccent?.text ?? LEGACY_CONTROL_KNOB,
    // The active menu item: the accent lifted until it reads on the dark app, like the
    // real --accent-on-dark; without an accent, the built-in pink.
    "--ds-accent-on-dark": ownAccent ? legibleOnDark(ownAccent.to) : accent.to,
    // The start stop as text (the portal's white → accent headings), like the real
    // --accent-from-on-dark; without an accent, the built-in purple.
    "--ds-accent-from-on-dark": ownAccent ? legibleOnDark(ownAccent.from) : accent.from,
  } as CSSProperties;
};

const ACCENT_SCOPE_PROPERTIES = [
  "--accent-from",
  "--accent-to",
  "--accent-text",
  "--accent-from-hover",
  "--accent-to-hover",
  "--accent-from-rgb",
  "--accent-to-rgb",
  "--accent-on-dark",
  "--accent-from-on-dark",
];

/**
 * The --accent-* variables the app's own controls read (the "accent" button, switches),
 * set on a Design Studio surface so they wear the palette being previewed. Never set on
 * the document: the real theme changes only once the studio is saved.
 *
 * A palette without an accent sets them to `initial`: that hides a saved accent on the
 * page around the surface, and each control falls back to its own built-in colours,
 * exactly as it does for a studio without an accent.
 */
export const buildDesignStudioAccentScope = (
  colors: IDesignStudioColors,
): CSSProperties => {
  const accent = getStudioAccent(colors);

  if (!accent) {
    return Object.fromEntries(
      ACCENT_SCOPE_PROPERTIES.map((name) => [name, "initial"]),
    ) as CSSProperties;
  }

  return {
    "--accent-from": accent.from,
    "--accent-to": accent.to,
    "--accent-text": accent.text,
    "--accent-from-hover": lightenHex(accent.from, 18),
    "--accent-to-hover": lightenHex(accent.to, 18),
    "--accent-from-rgb": hexToRgbTriplet(accent.from),
    "--accent-to-rgb": hexToRgbTriplet(accent.to),
    "--accent-on-dark": legibleOnDark(accent.to),
    "--accent-from-on-dark": legibleOnDark(accent.from),
  } as CSSProperties;
};
